import {
  AcknowledgementName,
  ComplianceEntityForm,
  ComplianceEntitySubjectType,
  ComplianceEntityTaxClassification,
  ComplianceEntityTaxIdentifierUpdate,
  ComplianceEntityType,
  GetMissingFieldsLevelParameter,
  OnboardingMissingFields,
  Stakeholder,
  StakeholderMemberType,
  VerificationLevel
} from "@wingspanhq/wingspan-api-sdk";
import {
  acknowledgementsApi,
  complianceEntitiesApi,
  onboardingApi,
  stakeholdersApi
} from "@api";
import {
  queriesComplianceEntities,
  queriesOnboarding,
  queriesStakeholders
} from "@query";
import { useSession } from "@hooks/useSession";
import { useWSMutation, WSMutationConfig } from "../../../../query/helpers";
import {
  acknowledgementVersions,
  bankingAccountAcknowledgements
} from "../../../../shared/constants/acknowledgements";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { FormPartialDataPerson } from "../../components/FormPartialPerson";
import { useOnboardignModules, useOnboardingContext } from "../../Context";
import { OnboardingModuleCreateAccount } from "../../types";
import { CreateAccountContext } from "./types";

export type CreateAccountSubmitResult = {
  status?: VerificationLevel;
  missingData: OnboardingMissingFields;
};

export const useMutationSubmit = (
  config?: WSMutationConfig<CreateAccountSubmitResult, WSServiceError, void>
) => {
  const session = useSession();
  const [data] = useOnboardingContext<CreateAccountContext>();

  const modules = useOnboardignModules();
  const onboardingModule = modules.find(m => m.type === "create_account") as
    | OnboardingModuleCreateAccount
    | undefined;

  return useWSMutation<CreateAccountSubmitResult, WSServiceError, void>(
    async () => {
      if (!data.accountType?.type || !data.accountType?.country) {
        throw new Error("Invalid form data");
      }

      const accountId = session.accountId;
      const country =
        onboardingModule?.options?.country || data.accountType.country;
      // TODO(v3): migrate verificationLevel type to GetMissingFieldsLevelParameter
      const level = (onboardingModule?.options?.verificationLevel ??
        "Tax") as GetMissingFieldsLevelParameter;
      const type =
        data.accountType.type === "Individual"
          ? ComplianceEntityType.INDIVIDUAL
          : ComplianceEntityType.BUSINESS;

      const entities = await complianceEntitiesApi.listComplianceEntities({});
      const primary = (entities.data ?? []).find(
        entity => !entity.subject && entity.type === type
      );

      if (data.accountType.type === "Individual") {
        if (!data.personalInformation) {
          throw new Error("No personal information set");
        }

        const person = data.personalInformation.person;
        const personCountry = person.country || country;

        if (primary) {
          await complianceEntitiesApi.updateComplianceEntity({
            complianceEntityId: primary.id,
            complianceEntityUpdate: {
              individualLegalName: {
                givenName: person.firstName || undefined,
                middleNames: person.middleName || undefined,
                familyName: person.lastName || undefined
              },
              physicalAddress: {
                line1: person.address.addressLine1 || undefined,
                line2: person.address.addressLine2 || undefined,
                city: person.address.city || undefined,
                state: person.address.state || undefined,
                postalCode: person.address.postalCode || undefined,
                country: personCountry
              }
            }
          });

          // Tax identifiers are add-only and can't ride a jurisdiction/country
          // change, so add any not-yet-present registrations in their own PATCH
          // separate from the address update above.
          const existingTaxIdKeys = new Set(
            (primary.taxIdentifiers ?? [])
              .filter(taxId => taxId.hasValue)
              .map(taxId => `${taxId.countryCode}:${taxId.type}`)
          );
          const candidateTaxIds: ComplianceEntityTaxIdentifierUpdate[] = [];
          if (person.taxId) {
            candidateTaxIds.push({
              countryCode: personCountry,
              type: personCountry === "US" ? "SSN" : "TIN",
              value: person.taxId
            });
          }
          const usPersonSsn =
            data.usInternationalPersonInformation?.usPerson?.ssn;
          if (usPersonSsn && personCountry !== "US") {
            candidateTaxIds.push({
              countryCode: "US",
              type: "SSN",
              value: usPersonSsn
            });
          }
          const taxIdentifiersToAdd = candidateTaxIds.filter(
            taxId =>
              !existingTaxIdKeys.has(`${taxId.countryCode}:${taxId.type}`)
          );
          if (taxIdentifiersToAdd.length > 0) {
            await complianceEntitiesApi.updateComplianceEntity({
              complianceEntityId: primary.id,
              complianceEntityUpdate: { taxIdentifiers: taxIdentifiersToAdd }
            });
          }
        } else {
          const taxIdentifiers = [];
          if (person.taxId) {
            taxIdentifiers.push({
              countryCode: personCountry,
              type: personCountry === "US" ? "SSN" : "TIN",
              value: person.taxId
            });
          }
          const usPersonSsn =
            data.usInternationalPersonInformation?.usPerson?.ssn;
          if (usPersonSsn && personCountry !== "US") {
            taxIdentifiers.push({
              countryCode: "US",
              type: "SSN",
              value: usPersonSsn
            });
          }

          await complianceEntitiesApi.createComplianceEntity({
            complianceEntityCreate: {
              type: ComplianceEntityType.INDIVIDUAL,
              jurisdictionCountry: personCountry,
              individualLegalName: {
                givenName: person.firstName || undefined,
                middleNames: person.middleName || undefined,
                familyName: person.lastName || undefined
              },
              dateOfBirth: person.birthday || undefined,
              physicalAddress: {
                line1: person.address.addressLine1 || undefined,
                line2: person.address.addressLine2 || undefined,
                city: person.address.city || undefined,
                state: person.address.state || undefined,
                postalCode: person.address.postalCode || undefined,
                country: personCountry
              },
              taxIdentifiers:
                taxIdentifiers.length > 0 ? taxIdentifiers : undefined
            }
          });
        }
      } else {
        if (!data.businessInformation) {
          throw new Error("No business information set");
        }
        if (!data.representative) {
          throw new Error("No representative set");
        }

        const business = data.businessInformation;

        if (primary) {
          await complianceEntitiesApi.updateComplianceEntity({
            complianceEntityId: primary.id,
            complianceEntityUpdate: {
              doingBusinessAs: business.dba || undefined,
              websiteUrl: business.noWebsite
                ? "None"
                : business.website || undefined,
              natureOfBusiness: business.industry || undefined,
              physicalAddress: {
                line1: business.address.addressLine1 || undefined,
                line2: business.address.addressLine2 || undefined,
                city: business.address.city || undefined,
                state: business.address.state || undefined,
                postalCode: business.address.postalCode || undefined,
                country
              }
            }
          });
        } else {
          let form: ComplianceEntityForm | undefined;
          let taxClassification: ComplianceEntityTaxClassification | undefined;
          switch (business.taxClassification) {
            case "SoleProprietorship":
              form = ComplianceEntityForm.SOLE_PROPRIETORSHIP;
              break;
            case "LlcSingleMember":
              form = ComplianceEntityForm.LIMITED_LIABILITY;
              taxClassification =
                ComplianceEntityTaxClassification.TAXED_AS_DISREGARDED_ENTITY;
              break;
            case "CorporationS":
              form = ComplianceEntityForm.CORPORATION;
              taxClassification =
                ComplianceEntityTaxClassification.TAXED_AS_S_CORPORATION;
              break;
            case "CorporationC":
              form = ComplianceEntityForm.CORPORATION;
              taxClassification =
                ComplianceEntityTaxClassification.TAXED_AS_C_CORPORATION;
              break;
            case "Partnership":
              form = ComplianceEntityForm.PARTNERSHIP;
              taxClassification =
                ComplianceEntityTaxClassification.TAXED_AS_PARTNERSHIP;
              break;
            case "LlcCorporationS":
              form = ComplianceEntityForm.LIMITED_LIABILITY;
              taxClassification =
                ComplianceEntityTaxClassification.TAXED_AS_S_CORPORATION;
              break;
            case "LlcCorporationC":
              form = ComplianceEntityForm.LIMITED_LIABILITY;
              taxClassification =
                ComplianceEntityTaxClassification.TAXED_AS_C_CORPORATION;
              break;
            case "LlcPartnership":
              form = ComplianceEntityForm.LIMITED_LIABILITY;
              taxClassification =
                ComplianceEntityTaxClassification.TAXED_AS_PARTNERSHIP;
              break;
            case "NotForProfitOrganization":
              form = ComplianceEntityForm.NON_PROFIT;
              break;
          }

          await complianceEntitiesApi.createComplianceEntity({
            complianceEntityCreate: {
              type: ComplianceEntityType.BUSINESS,
              jurisdictionCountry: country,
              form,
              taxClassification,
              legalName: business.legalBusinessName || undefined,
              doingBusinessAs: business.dba || undefined,
              websiteUrl: business.noWebsite
                ? "None"
                : business.website || undefined,
              natureOfBusiness: business.industry || undefined,
              physicalAddress: {
                line1: business.address.addressLine1 || undefined,
                line2: business.address.addressLine2 || undefined,
                city: business.address.city || undefined,
                state: business.address.state || undefined,
                postalCode: business.address.postalCode || undefined,
                country
              },
              formationJurisdiction: business.stateOfIncorporation
                ? {
                    countryCode: country,
                    subdivisionCode: business.stateOfIncorporation
                  }
                : undefined,
              taxIdentifiers: business.taxId
                ? [{ countryCode: country, type: "EIN", value: business.taxId }]
                : undefined
            }
          });
        }

        const stakeholders = await stakeholdersApi.listAccountStakeholders({
          accountId
        });
        const existingStakeholders = stakeholders.data ?? [];

        await upsertStakeholderIdentity({
          accountId,
          country,
          existing: existingStakeholders.find(
            stakeholder =>
              stakeholder.memberType === StakeholderMemberType.PRINCIPAL
          ),
          memberType: StakeholderMemberType.PRINCIPAL,
          isController: true,
          person: data.representative.representative
        });

        for (const beneficialOwner of data.representative.beneficialOwners ||
          []) {
          await upsertStakeholderIdentity({
            accountId,
            country,
            existing: existingStakeholders.find(
              stakeholder =>
                stakeholder.memberType === StakeholderMemberType.PERSON &&
                stakeholder.email === beneficialOwner.email
            ),
            memberType: StakeholderMemberType.PERSON,
            person: beneficialOwner
          });
        }
      }

      const signUpAcknowledgements =
        level === GetMissingFieldsLevelParameter.BANKING
          ? bankingAccountAcknowledgements
          : level === GetMissingFieldsLevelParameter.TAX
            ? [
                AcknowledgementName.WINGSPAN_PRIVACY_POLICY_ACCEPTANCE,
                AcknowledgementName.WINGSPAN_TOS_ACCEPTANCE
              ]
            : [];

      for (const name of signUpAcknowledgements) {
        await acknowledgementsApi.acceptAcknowledgement({
          acknowledgementName: name,
          acceptAcknowledgementRequest: {
            version: acknowledgementVersions[name]
          }
        });
      }

      await onboardingApi.triggerVerification({ verifyRequest: { level } });

      const [status, missingData] = await Promise.all([
        onboardingApi.getOnboardingStatus({}),
        onboardingApi.getMissingFields({ level })
      ]);

      return {
        status: status.verifications?.tax,
        missingData
      };
    },
    {
      dependencies: [
        queriesComplianceEntities.listComplianceEntities.queryKeyBase,
        queriesStakeholders.listAccountStakeholders.queryKeyBase,
        queriesOnboarding.getOnboardingStatus.queryKeyBase,
        queriesOnboarding.getMissingFields.queryKeyBase
      ],
      ...config
    }
  );
};

export async function upsertStakeholderIdentity(params: {
  accountId: string;
  country: string;
  existing?: Stakeholder;
  memberType: StakeholderMemberType;
  isController?: boolean;
  person: FormPartialDataPerson;
}) {
  const { accountId, country, existing, memberType, isController, person } =
    params;
  const ownershipPercentage = person.ownershipStake ?? 0;
  const personCountry = person.country || country;

  let stakeholderId: string;

  if (existing?.id) {
    stakeholderId = existing.id;
    await stakeholdersApi.updateStakeholder({
      accountId,
      stakeholderId,
      stakeholderUpdate: {
        email: person.email || undefined,
        ownershipPercentage,
        isController
      }
    });
  } else {
    const created = await stakeholdersApi.createAccountStakeholder({
      accountId,
      stakeholderCreate: {
        memberType,
        email: person.email || undefined,
        ownershipPercentage,
        isController
      }
    });
    stakeholderId = created.id as string;
  }

  if (existing?.accountSuppliedComplianceEntityId) {
    const complianceEntityId = existing.accountSuppliedComplianceEntityId;
    await complianceEntitiesApi.updateComplianceEntity({
      complianceEntityId,
      complianceEntityUpdate: {
        individualLegalName: {
          givenName: person.firstName || undefined,
          middleNames: person.middleName || undefined,
          familyName: person.lastName || undefined
        },
        physicalAddress: {
          line1: person.address.addressLine1 || undefined,
          line2: person.address.addressLine2 || undefined,
          city: person.address.city || undefined,
          state: person.address.state || undefined,
          postalCode: person.address.postalCode || undefined,
          country: personCountry
        }
      }
    });

    // Tax identifiers are add-only and can't ride the address update above, so
    // add a missing registration in its own PATCH. Read the entity first to
    // skip a registration that already exists (re-adding it 409s).
    if (person.taxId) {
      const taxIdType = personCountry === "US" ? "SSN" : "TIN";
      const entity = await complianceEntitiesApi.getComplianceEntity({
        complianceEntityId
      });
      const alreadyPresent = (entity.taxIdentifiers ?? []).some(
        taxId =>
          taxId.hasValue &&
          taxId.countryCode === personCountry &&
          taxId.type === taxIdType
      );
      if (!alreadyPresent) {
        await complianceEntitiesApi.updateComplianceEntity({
          complianceEntityId,
          complianceEntityUpdate: {
            taxIdentifiers: [
              {
                countryCode: personCountry,
                type: taxIdType,
                value: person.taxId
              }
            ]
          }
        });
      }
    }
  } else {
    const entity = await complianceEntitiesApi.createComplianceEntity({
      complianceEntityCreate: {
        type: ComplianceEntityType.INDIVIDUAL,
        jurisdictionCountry: personCountry,
        subject: {
          type: ComplianceEntitySubjectType.STAKEHOLDER,
          id: stakeholderId
        },
        individualLegalName: {
          givenName: person.firstName || undefined,
          middleNames: person.middleName || undefined,
          familyName: person.lastName || undefined
        },
        dateOfBirth: person.birthday || undefined,
        physicalAddress: {
          line1: person.address.addressLine1 || undefined,
          line2: person.address.addressLine2 || undefined,
          city: person.address.city || undefined,
          state: person.address.state || undefined,
          postalCode: person.address.postalCode || undefined,
          country: personCountry
        },
        taxIdentifiers: person.taxId
          ? [
              {
                countryCode: personCountry,
                type: personCountry === "US" ? "SSN" : "TIN",
                value: person.taxId
              }
            ]
          : undefined
      }
    });
    await stakeholdersApi.updateStakeholder({
      accountId,
      stakeholderId,
      stakeholderUpdate: { accountSuppliedComplianceEntityId: entity.id }
    });
  }
}
